import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View, ScrollView, TouchableOpacity } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import moment from 'moment';
import { useScale, ScaleFn , useDarkMode} from './common';
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
  bg:        '#0F172A',
  card:      '#1E293B',
  cardInner: '#0F172A',
  border:    'rgba(99,179,237,0.12)',
  text:      '#E2E8F0',
  textSub:   '#94A3B8',
  muted:     '#a8adb497',
};
const LIGHT = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  cardInner: '#F3F4F6',
  border:    '#E5E7EB',
  text:      '#111827',
  textSub:   '#4B5563',
  muted:     '#9CA3AF',
};
const S = {
  accent:  '#38BDF8',
  ok:      '#34D399',
  err:     '#F87171',
  warn:    '#FB923C',
  replace: '#FBBF24',
  purple:  '#A78BFA',
  pink:    '#F472B6',
};
const WEEK = ['일', '월', '화', '수', '목', '금', '토'];

export default function Main({ navigation }: any) {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const { dark, setDark } = useDarkMode();
  const [adminId, setAdminId] = useState('관리자');
  const [hospitalName, setHospitalName] = useState('');   // HOSPITAL.NAME (지점마다 다름)
  const [today, setToday] = useState('');
  const [caregiver, setCaregiver] = useState(0);
  const [pending, setPending] = useState(0);
  const [areas, setAreas] = useState<any[]>([]);
  const [alarms, setAlarms] = useState<any[]>([]);
  const [availW, setAvailW] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const T = dark ? DARK : LIGHT;

  const load = () => {
    Data.fetchData('group/getCaregiverList', { AREA: '' }, (r: any, c: any) => setCaregiver(c > 0 ? (r?.length || 0) : 0), true);
    Data.fetchData('group/getManagerList', { APPROVAL: 0 }, (r: any, c: any) => setPending(c > 0 ? (r?.length || 0) : 0), true);
    Data.fetchData('group/getAreaList', {}, (r: any, c: any) => setAreas(c > 0 ? (r || []) : []), true);
    Data.fetchData('group/getAlarmList', {}, (r: any, c: any) => setAlarms(c > 0 ? (r || []) : []), true);
  };

  // 새로고침 = 데이터 재요청 + 시각 갱신 + 잠깐 피드백
  const refresh = () => {
    setRefreshing(true);
    const d = new Date();
    setToday(`${moment().format('YYYY.MM.DD')} (${WEEK[d.getDay()]}) ${moment().format('HH:mm:ss')}`);
    load();
    setTimeout(() => setRefreshing(false), 600);
  };

  useEffect(() => {
    if (!isFocused) return;
    getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    getStorage('AutoLogin').then((v: any) => { if (v?.id) setAdminId(v.id); });
    getStorage('Session').then((v: any) => {
      if (v?.id) setAdminId(v.id);                       // 자동로그인 미체크여도 세션엔 항상 있음
      const hn = v?.hospital?.[0]?.NAME;
      if (hn) setHospitalName(hn);
    });
    refresh();
    const id = setInterval(refresh, 30000); // 30초 자동 갱신
    return () => clearInterval(id);
  }, [isFocused]);

  // ─── 집계 ───
  const totalSensor  = areas.reduce((a, i) => a + (i.SENSOR ?? 0), 0);
  const offSensor    = areas.reduce((a, i) => a + (i.S_DISCONNECT ?? 0), 0);
  const offRelay     = areas.reduce((a, i) => a + (i.R_DISCONNECT ?? 0), 0);
  const alarmOf = (area: string) => alarms.filter((x: any) => x.AREA === area).length;

  const STATS = [
    { icon: 'people',              label: '등록 보호사',      value: caregiver,   unit: '명', color: S.accent,  to: 'Members' },
    { icon: 'time-outline',        label: '승인 대기',        value: pending,     unit: '건', color: S.warn,    to: 'Management' },
    { icon: 'radio',               label: '등록 센서',        value: totalSensor, unit: '대', color: S.ok,      to: 'Sensor' },
    { icon: 'notifications',       label: '알림 센서',        value: alarms.length, unit: '건', color: S.err,     to: 'SensorScreen' },
    { icon: 'pulse',               label: '연결 끊김 센서',   value: offSensor,   unit: '대', color: S.pink,    to: 'SensorScreen' },
    { icon: 'wifi',                label: '연결 끊김 중계기', value: offRelay,    unit: '대', color: S.purple,  to: 'Repeater' },
  ];

  const go = (to: string) => { try { navigation.navigate(to); } catch (e) { } };

  // ─── 반응형 열 계산 ───
  const cw = (availW || 1100) - s(40);
  const statCols = cw < 520 ? 2 : cw < 860 ? 3 : 6;
  const statW = Math.floor((cw - s(16) * (statCols - 1)) / statCols);
  const zoneCols = cw < 520 ? 1 : cw < 780 ? 2 : cw < 1050 ? 3 : cw < 1300 ? 4 : 5;
  const zoneW = Math.floor((cw - s(14) * (zoneCols - 1)) / zoneCols);

  return (
    <View style={[styles.container, { backgroundColor: T.bg }]}>
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}
        onLayout={(e) => { const w = e.nativeEvent.layout.width; if (w) setAvailW(w); }}>

        {/* ── 환영 헤더 ── */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: s(10) }}>
          <View>
            <Text style={{ fontSize: s(24), fontWeight: '800', color: T.text }}>{hospitalName ? hospitalName + ' ' : ''}{adminId}님, 환영합니다! 👋</Text>
            <Text style={{ fontSize: s(14), color: T.textSub, marginTop: s(6) }}>오늘도 안전하고 스마트한 돌봄 환경을 만들어 가세요.</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
            <View style={[styles.dateChip, { backgroundColor: T.card, borderColor: T.border }]}>
              <Ionicons name="calendar-outline" size={s(14)} color={T.textSub} style={{ marginRight: s(6) }} />
              <Text style={{ fontSize: s(13), color: T.textSub, fontWeight: '600' }}>{today}</Text>
            </View>
            <TouchableOpacity style={[styles.iconBtn, { backgroundColor: S.accent, opacity: refreshing ? 0.6 : 1 }]} onPress={refresh} disabled={refreshing}>
              <Ionicons name={refreshing ? "sync" : "refresh"} size={s(15)} color="#fff" />
              <Text style={{ fontSize: s(13), fontWeight: '700', color: '#fff', marginLeft: s(5) }}>{refreshing ? '갱신중…' : '새로고침'}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* ── 통계 카드 6개 ── */}
        <View style={styles.rowWrap}>
          {STATS.map(st => (
            <TouchableOpacity key={st.label} activeOpacity={0.85} onPress={() => go(st.to)}
              style={[styles.card, styles.noGrow, { width: statW, backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={[styles.statIcon, { backgroundColor: st.color + '18' }]}>
                  <Ionicons name={st.icon as any} size={s(20)} color={st.color} />
                </View>
                <Ionicons name="chevron-forward" size={s(16)} color={T.muted} />
              </View>
              <Text style={{ fontSize: s(13), color: T.textSub, fontWeight: '600', marginTop: s(12) }} numberOfLines={1}>{st.label}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginTop: s(2) }}>
                <Text style={{ fontSize: s(26), fontWeight: '800', color: T.text }}>{(st.value || 0).toLocaleString()}</Text>
                <Text style={{ fontSize: s(14), color: T.textSub, fontWeight: '600', marginLeft: s(3), marginBottom: s(4) }}>{st.unit}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>

        {/* ── 구역별 현황 ── */}
        <View style={styles.sectionHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
            <Ionicons name="business-outline" size={s(18)} color={S.accent} />
            <Text style={{ fontSize: s(17), fontWeight: '800', color: T.text }}>구역별 현황</Text>
            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
              <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{areas.length}</Text>
            </View>
          </View>
          <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: s(2) }} onPress={() => go('SensorScreen')}>
            <Text style={{ fontSize: s(13), fontWeight: '600', color: S.accent }}>더 보기</Text>
            <Ionicons name="chevron-forward" size={s(14)} color={S.accent} />
          </TouchableOpacity>
        </View>

        <View style={styles.rowWrap}>
          {areas.length === 0 ? (
            <Text style={{ fontSize: s(14), color: T.muted, paddingVertical: s(24) }}>등록된 구역이 없습니다.</Text>
          ) : areas.map((a, i) => {
            const rOk = a.R_CONNECT ?? 0, rBad = a.R_DISCONNECT ?? 0;
            const sOk = a.S_CONNECT ?? 0, sBad = a.S_DISCONNECT ?? 0;
            const alarmCnt = alarmOf(a.AREA);
            const warn = rBad + sBad + alarmCnt;
            return (
              <TouchableOpacity key={a.AREA || i} activeOpacity={0.85}
                onPress={() => { try { navigation.navigate('SensorScreenDetail', { data: a, area: a.AREA }); } catch (e) { } }}
                style={[styles.card, styles.noGrow, styles.zoneCard, { width: zoneW, backgroundColor: T.card, borderColor: T.border, borderLeftColor: warn > 0 ? S.err : S.ok, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: s(12) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(6), flex: 1 }}>
                    <Ionicons name="home" size={s(15)} color={T.textSub} />
                    <Text style={{ fontSize: s(14), fontWeight: '700', color: T.text }} numberOfLines={1}>{a.AREA || `구역${i + 1}`}</Text>
                  </View>
                  <View style={{ backgroundColor: (warn > 0 ? S.err : S.ok) + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(8) }}>
                    <Text style={{ fontSize: s(11), fontWeight: '800', color: warn > 0 ? S.err : S.ok }}>경고 {warn}</Text>
                  </View>
                </View>
                <View style={styles.zoneRow}>
                  <Text style={{ fontSize: s(12), color: T.textSub, fontWeight: '600' }}>중계기</Text>
                  <View style={{ flexDirection: 'row', gap: s(10) }}>
                    <Text style={{ fontSize: s(12), fontWeight: '700', color: S.ok }}>연결 {rOk}</Text>
                    <Text style={{ fontSize: s(12), fontWeight: '700', color: rBad > 0 ? S.err : T.muted }}>끊김 {rBad}</Text>
                  </View>
                </View>
                <View style={styles.zoneRow}>
                  <Text style={{ fontSize: s(12), color: T.textSub, fontWeight: '600' }}>센서</Text>
                  <View style={{ flexDirection: 'row', gap: s(10) }}>
                    <Text style={{ fontSize: s(12), fontWeight: '700', color: S.ok }}>연결 {sOk}</Text>
                    <Text style={{ fontSize: s(12), fontWeight: '700', color: sBad > 0 ? S.err : T.muted }}>끊김 {sBad}</Text>
                  </View>
                </View>
                <View style={[styles.zoneMore, { borderTopColor: T.border }]}>
                  <Text style={{ fontSize: s(12), fontWeight: '600', color: S.accent }}>상세 보기</Text>
                  <Ionicons name="chevron-forward" size={s(13)} color={S.accent} />
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  container: { flex: 1 },
  scroll: { padding: s(20), gap: s(16) },
  card: {
    borderRadius: s(14), borderWidth: 1, padding: s(16),
    shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: s(12), elevation: 3,
  },
  noGrow: { flexGrow: 0, flexShrink: 0 },
  rowWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: s(14) },
  statIcon: { width: s(42), height: s(42), borderRadius: s(12), justifyContent: 'center', alignItems: 'center' },
  dateChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: s(12), height: s(36), borderRadius: s(10), borderWidth: 1 },
  iconBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: s(14), height: s(36), borderRadius: s(10), justifyContent: 'center' },
  iconOnly: { width: s(36), height: s(36), borderRadius: s(10), justifyContent: 'center', alignItems: 'center' },
  sectionHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: s(4) },
  zoneCard: { borderLeftWidth: 3 },
  zoneRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: s(6) },
  zoneMore: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: s(2), marginTop: s(8), paddingTop: s(8), borderTopWidth: 1 },
});
